import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, ShieldCheck, Info, Sprout, Utensils, HeartHandshake, MapPin, Loader2 } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import OrganicShapes from "@/components/OrganicShapes";
import { searchPublic, trackById, fetchPublicDrives } from "@/lib/api";
import { cldImg } from "@/lib/img";
import { formatDate } from "@/lib/date";

const HELPER_BLOCKS = [
    {
        t: "What you can search",
        items: [
            "A Drive ID — e.g. SAJJAN-JUL26",
            "A Tree ID — e.g. TREE-1, TREE-248",
            "Or a name / place — results appear live",
        ],
    },
    {
        t: "What a drive shows",
        items: [
            "Every tree it planted, individually",
            "Food packets & beneficiaries",
            "Children supported & items given",
        ],
    },
    {
        t: "What you can verify",
        items: [
            "Each tree's species, status & photos",
            "A dated monitoring timeline",
            "All public — no login required",
        ],
    },
];

function CauseChip({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-seedh-surface-green/60 text-seedh-text px-2.5 py-0.5 text-[0.7rem] font-medium">
            <Icon className="w-3 h-3 text-seedh-green" /> {label}
        </span>
    );
}

function DriveCard({ d, i }) {
    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }}>
            <Link to={`/drive/${encodeURIComponent(d.id)}`} className="group card-base bg-white p-5 hover:border-seedh-blue transition-colors flex flex-col h-full">
                {d.image && <img src={cldImg(d.image, 400)} alt="" loading="lazy" className="rounded-xl w-full h-36 object-cover mb-4 border border-seedh-border" />}
                <span className="font-mono text-[0.7rem] text-seedh-blue">{d.id}</span>
                <span className="font-display text-xl text-seedh-text mt-1">{d.title}</span>
                {(d.location || d.date) && (
                    <span className="text-xs text-seedh-muted mt-1 inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> {[d.location, formatDate(d.date)].filter(Boolean).join(" · ")}
                    </span>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {d.causes?.trees > 0 && <CauseChip icon={Sprout} label={`${d.causes.trees} trees`} />}
                    {d.causes?.foodPackets > 0 && <CauseChip icon={Utensils} label={`${d.causes.foodPackets} food`} />}
                    {d.causes?.children > 0 && <CauseChip icon={HeartHandshake} label={`${d.causes.children} children`} />}
                </div>
                <div className="mt-auto pt-4 text-xs text-seedh-blue inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    View drive <ArrowRight className="w-3.5 h-3.5" />
                </div>
            </Link>
        </motion.div>
    );
}

function TreeRow({ t }) {
    return (
        <Link to={`/tree/${encodeURIComponent(t.id)}`} className="group flex items-center gap-3 rounded-xl border border-seedh-border bg-white px-4 py-3 hover:border-seedh-green transition-colors">
            <span className="w-9 h-9 rounded-lg bg-seedh-surface-green/60 flex items-center justify-center shrink-0">
                <Sprout className="w-4.5 h-4.5 text-seedh-green" />
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-seedh-text">{t.id}</span>
                    <span className="text-xs rounded-full bg-seedh-surface-green/50 text-seedh-green px-2 py-0.5">{t.status || "—"}</span>
                </div>
                {t.species && <div className="text-xs text-seedh-muted truncate mt-0.5">{t.species}</div>}
            </div>
            <ArrowRight className="w-4 h-4 text-seedh-blue shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </Link>
    );
}

export default function Tracker() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({ drives: [], trees: [] });
    const [searching, setSearching] = useState(false);
    const [notFound, setNotFound] = useState("");
    const [drives, setDrives] = useState([]);
    const [loadingDrives, setLoadingDrives] = useState(true);
    const seq = useRef(0);

    // Browse list (shown when the search box is empty).
    useEffect(() => {
        let alive = true;
        fetchPublicDrives({ limit: 60 })
            .then((d) => alive && setDrives(d))
            .catch(() => alive && setDrives([]))
            .finally(() => alive && setLoadingDrives(false));
        return () => {
            alive = false;
        };
    }, []);

    // Live, debounced search as the user types.
    useEffect(() => {
        const q = query.trim();
        setNotFound("");
        if (!q) {
            setResults({ drives: [], trees: [] });
            setSearching(false);
            return;
        }
        setSearching(true);
        const mine = ++seq.current;
        const t = setTimeout(async () => {
            try {
                const data = await searchPublic(q);
                if (mine === seq.current) setResults(data);
            } catch {
                if (mine === seq.current) setResults({ drives: [], trees: [] });
            } finally {
                if (mine === seq.current) setSearching(false);
            }
        }, 220);
        return () => clearTimeout(t);
    }, [query]);

    // Enter → jump straight to an exact ID match if there is one.
    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            const id = query.trim();
            if (!id) return;
            try {
                const data = await trackById(id);
                navigate(data.kind === "tree" ? `/tree/${encodeURIComponent(data.id)}` : `/drive/${encodeURIComponent(data.id)}`);
            } catch {
                if (results.drives.length === 0 && results.trees.length === 0) {
                    setNotFound(`No drive or tree found for "${id}". Check the ID and try again.`);
                }
            }
        },
        [query, navigate, results],
    );

    const hasQuery = query.trim().length > 0;
    const hasResults = results.drives.length > 0 || results.trees.length > 0;

    return (
        <main data-testid="tracker-page" className="bg-white pt-32 pb-24 relative overflow-hidden">
            <OrganicShapes variant="mixed" />

            <section className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <SectionHeading
                    eyebrow="Impact tracker"
                    heading={["Track any drive or tree,", "as you type."]}
                    italicIndex={1}
                    description="Start typing a Drive ID, Tree ID, name or place — matches appear live below. Open a drive to see everything it delivered, or a tree to follow its journey."
                />

                {/* Search */}
                <SectionReveal delay={0.15}>
                    <div className="mt-12 card-base bg-white p-5 md:p-7">
                        <form
                            onSubmit={handleSubmit}
                            className="flex items-center rounded-full border border-seedh-border bg-white pl-5 pr-2 py-2 focus-within:border-seedh-blue focus-within:shadow-[0_0_0_4px_rgba(4,88,134,0.08)] transition-all"
                        >
                            {searching ? <Loader2 className="w-4 h-4 text-seedh-blue animate-spin" /> : <Search className="w-4 h-4 text-seedh-muted" />}
                            <input
                                data-testid="tracker-search-input"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Start typing… e.g. SAJJAN-JUL26, TREE-1, Neem, Udaipur"
                                className="flex-1 bg-transparent px-3 py-2 text-base placeholder:text-seedh-muted/70 focus:outline-none"
                                autoComplete="off"
                            />
                            <button type="submit" data-testid="tracker-search-submit" className="btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                                Track <ArrowRight className="w-4 h-4" />
                            </button>
                        </form>
                        <div className="mt-4 text-xs text-seedh-muted inline-flex items-center gap-2">
                            <ShieldCheck className="w-3.5 h-3.5 text-seedh-green" /> Public lookup · no login required
                        </div>
                        {notFound && (
                            <div data-testid="tracker-notfound" className="mt-4 rounded-xl border border-seedh-border bg-seedh-surface-beige/50 px-4 py-3 text-sm text-seedh-text inline-flex items-center gap-2">
                                <Info className="w-4 h-4 text-seedh-brown shrink-0" /> {notFound}
                            </div>
                        )}
                    </div>
                </SectionReveal>

                {/* Live results (while typing) */}
                {hasQuery ? (
                    <div className="mt-8" data-testid="tracker-results">
                        {!hasResults && !searching ? (
                            <div data-testid="tracker-empty-state" className="rounded-2xl border border-dashed border-seedh-border p-12 text-center bg-seedh-surface-beige/40">
                                <Info className="w-7 h-7 text-seedh-brown mx-auto" />
                                <div className="font-display text-2xl text-seedh-text mt-4">No matches yet</div>
                                <p className="text-sm text-seedh-muted mt-2 max-w-md mx-auto">Try a Drive ID, a Tree ID, or part of a name or location.</p>
                            </div>
                        ) : (
                            <AnimatePresence mode="popLayout">
                                {results.drives.length > 0 && (
                                    <motion.div key="d" layout>
                                        <h3 className="text-[0.7rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold mb-3">Drives ({results.drives.length})</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                            {results.drives.map((d, i) => <DriveCard key={d.id} d={d} i={i} />)}
                                        </div>
                                    </motion.div>
                                )}
                                {results.trees.length > 0 && (
                                    <motion.div key="t" layout className="mt-8">
                                        <h3 className="text-[0.7rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold mb-3">Trees ({results.trees.length})</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {results.trees.map((t) => <TreeRow key={t.id} t={t} />)}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        )}
                    </div>
                ) : (
                    /* Browse drives (empty search) */
                    <>
                        <SectionReveal delay={0.2}>
                            <div className="mt-14 flex items-center justify-between gap-4 flex-wrap">
                                <h2 className="font-display text-2xl text-seedh-text">Browse drives</h2>
                                <div className="text-[0.7rem] tracking-[0.22em] uppercase text-seedh-green">Public · verifiable</div>
                            </div>
                        </SectionReveal>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tracker-drives">
                            {loadingDrives ? (
                                <div className="col-span-full rounded-2xl border border-dashed border-seedh-border p-12 text-center text-seedh-muted">Loading drives…</div>
                            ) : drives.length === 0 ? (
                                <div className="col-span-full rounded-2xl border border-dashed border-seedh-border p-12 text-center bg-seedh-surface-beige/40">
                                    <Sprout className="w-7 h-7 text-seedh-brown mx-auto" />
                                    <div className="font-display text-2xl text-seedh-text mt-4">No public drives yet.</div>
                                    <p className="text-sm text-seedh-muted mt-2 max-w-md mx-auto">Once drives are published they'll appear here to browse and verify.</p>
                                </div>
                            ) : (
                                drives.map((d, i) => <DriveCard key={d.id} d={d} i={i} />)
                            )}
                        </div>
                    </>
                )}

                {/* Helper grid */}
                <SectionReveal delay={0.15}>
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {HELPER_BLOCKS.map((b, idx) => (
                            <motion.div key={b.t} whileHover={{ y: -4 }} className="card-base p-6 bg-seedh-surface-blue/40" data-testid={`tracker-helper-${idx}`}>
                                <div className="text-[0.68rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold">{b.t}</div>
                                <ul className="mt-4 space-y-2">
                                    {b.items.map((it) => (
                                        <li key={it} className="text-sm text-seedh-text flex items-start gap-2">
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-seedh-green mt-1.5" />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </SectionReveal>
            </section>
        </main>
    );
}
